import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { FirEditGtapsAttributesService } from './fir-edit-gtaps-attributes.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-fir-edit-gtaps-attributes',
    templateUrl: './fir-edit-gtaps-attributes.component.html',
    styleUrls: ['./fir-edit-gtaps-attributes.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FirEditGtapsAttributesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;
  
  // Initialization guard
  private isInitialized = false;

  // Entity data properties
  entityList: any[] = [];
  modalTitle = "Show FIR: GTAPS Attribute Editor";

  // Component data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  // Form data properties
  hoLeid: string = '';
  taxYearBegin: string = "2016-1";
  taxYearEnd: string = '';
  entityName: string = '';
  hoLeidEntityName: string = '';
  hoTaxCode: string = '';
  scenario: string = "RAF/BH";
  baseCurrency: string = "USD";
  funcCurrency: string = '';
  funcCurrDescription: string = '';
  funcCurrDisplay: string = '';
  
  // Summary values
  total959c3EPPoolFuncCurr: number = 0;
  totalPtiFuncCurr: number = 0;
  total959c3EPPoolUSD: number = 0;
  totalPtiUSD: number = 0;
  totalTaxPool: number = 0;
  totalUSDBasisInPti: number = 0;
  effectiveRateOn959c3TotalEp: number = 0;
  estimatedPtiExchangeGainLoss: number = 0;
  recaptureAccounts: string = "No";
  qualifiedDeficits: string = "No";
  examGhostScenario: string | null = null;
  rafGhostScenario: string | null = null;

  // Adjustment fields
  adjDesc: string = '';
  isG2G: string = 'Y';
  
  // Computed property for checkbox
  get isG2GChecked(): boolean {
    return this.isG2G === 'Y';
  }
  
  set isG2GChecked(value: boolean) {
    this.isG2G = value ? 'Y' : 'N';
  }

  // Labels
  gtapsBalanceHeader: string = '';
  labelSaveAndUpdate: string = "Save and Update";
  labelReset: string = "Reset";
  labelCancel: string = "Cancel";
  labelSummary: string = "Summary";
  labelTotal959c3EPPoolFuncCurr: string = '';
  labelTotalPtiFuncCurr: string = '';
  labelTotal959c3EPPoolUSD: string = '';
  labelTotalPtiUSD: string = '';
  labelTotalTaxPool: string = "Total Tax Pool";
  labelTotalUSDBasisInPti: string = "Total USD Basis in PTI";
  labelEffectiveRateOn959c3TotalEp: string = "Effective Rate on 959(c)(3) Total E&P";
  labelEstimatedPtiExchangeGainLoss: string = "Estimated PTI Exchange Gain/(Loss)";
  labelRecaptureAccts: string = "Recapture Accounts";
  labelQualDefs: string = "Qualified Deficits";
  labelAdjDesc: string = "Adjustment Description";
  labelThisProspectiveAdj: string = "This is a prospective adjustment";
  labelprospectiveAdjTitle: string = '';
  labelYear: string = "Year";
  labelAdjType: string = "Adjustment Type";
  labelTotal: string = "Total";
  labelTotals: string = "Totals";
  labelOwnSub: string = "Own/Sub";
  labelPtiType: string = "PTI-Type";
  labelPtiGenYr: string = "PTI Generated Year";
  labelAdjRelToYr: string = "Adjustment Related to Year";

  // Section headers
  foreignC3Header: string = '';
  foreignC3TaxesHeader: string = '';
  hdInForeignC3Header: string = '';
  hdInForeignC3TaxesHeader: string = '';
  ptiFCHeader: string = '';
  ptiUSDHeader: string = '';
  recapAcctHeader: string = '';
  qualDefHeader: string = '';
  otherAttrsHeader: string = '';

  // Error texts
  adjDescError: string = "Please enter adjustment description to the changes made!";
  noChangesError: string = "No changes made to the edit screen!";
  selectTaxYearError: string = "Select tax year for ";
  selectAdjTypeError: string = "Select adjustment type for ";
  selectOwnSubError: string = "Select own/sub for ";
  selectPtiTypeError: string = "Select PTI-Type for ";
  totalHFC3NegativeError: string = "Total hovering foreign C3 should be negative!";
  totalHC3TaxesPositiveError: string = "Total hovering C3 taxes should be positive!";
  adjustmentError: string = " Adjustment ";
  headerSectionForError: any = {};
  saveSuccess: string = "Data saved successfully.";

  // Drop down selection lists
  taxYearSL: any[] = [];
  adjTypeSL: any[] = [];
  adjTypeTaxesSL: any[] = [];
  ownSubSL: any[] = [
    {label:'OWN', value:'OWN'},
    {label:'SUB', value:'SUB'}
  ];
  ptiTypeSL: any[] = [
    {label:'C1A', value:'C1A'},
    {label:'C1B', value:'C1B'},
    {label:'C2', value:'C2'}
  ];

  // Data Lists
  allTotals: any = {};
  editableTotals: any = {};
  gtapsAdjustmentTypes: any = {};
  gtapsHeadings: any = {};
  editAttrsData: any[] = [];

  // To store list of header key, row no., basket desc which are dirtied/changed in this session
  changedHeaderRowBaskets: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackBarService: GtwSnackbarService,
    private firEditGtapsAttributesService: FirEditGtapsAttributesService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  init() {
    try {
      // Only initialize when we have ALL required data
      if (this.rowData && Object.keys(this.rowData).length > 0 && 
          this.filterParams && Object.keys(this.filterParams).length > 0 &&
          this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url) {
        
        // Guard against multiple calls
        if (this.isInitialized) {
          console.log('Component already initialized, skipping duplicate initialization');
          return;
        }
        
        console.log('Initializing component with all required data available');
        this.setupEntityData();
        this.setupFormData();
        this.setupLabels();
        this.loadGTAPSEditAttrsData();
        
        this.isInitialized = true;
      } else {
        console.log('Waiting for all required data before initialization:', {
          hasRowData: !!(this.rowData && Object.keys(this.rowData).length > 0),
          hasFilterParams: !!(this.filterParams && Object.keys(this.filterParams).length > 0),
          hasBaseUrl: !!(this.GENERAL_CONFIG && this.GENERAL_CONFIG.base_url)
        });
      }
    } catch (e) {
      console.error('Error in FirEditGtapsAttributes init:', e);
    }
  }

  private setupEntityData() {
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID || this.rowData.GTAPS_LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  private setupFormData() {
    this.hoLeid = this.rowData.GTAPS_LEID;
    this.entityName = this.rowData.LE_NAME;
    this.hoLeidEntityName = this.hoLeid + " - " + this.entityName;
    this.hoTaxCode = this.rowData.GTAPS_CDR_NO;
    this.funcCurrency = this.rowData.FUNCTIONAL_CURRENCY;
    this.funcCurrDescription = this.rowData.CURR_DESC;
    this.funcCurrDisplay = this.funcCurrency + " - " + this.funcCurrDescription;
    
    // Summary values from rowData
    this.total959c3EPPoolFuncCurr = this.rowData.POST_1986C3EP_FC || 0;
    this.totalPtiFuncCurr = this.rowData.PTIFC || 0;
    this.total959c3EPPoolUSD = this.rowData.POST_1986C3EPUSD || 0;
    this.totalPtiUSD = this.rowData.PTIUSD || 0;
    this.totalTaxPool = this.rowData.POST_1986C3TAXES || 0;
    this.totalUSDBasisInPti = this.rowData.PTI_USD_BASIS_TOT || 0;
    this.effectiveRateOn959c3TotalEp = this.rowData.ETRONPOST_1986C3EP || 0;
    this.estimatedPtiExchangeGainLoss = this.rowData.PTI_EXCHG_GAIN_LOSS || 0;
  }

  private setupLabels() {
    this.labelTotal959c3EPPoolFuncCurr = `Total Section 959(c)(3) E&P Pool (Functional Currency = ${this.funcCurrency})`;
    this.labelTotalPtiFuncCurr = `Total PTI (Functional Currency = ${this.funcCurrency})`;
    this.labelTotal959c3EPPoolUSD = `Total Section 959(c)(3) E&P Pool (Currency = ${this.baseCurrency})`;
    this.labelTotalPtiUSD = `Total PTI (Currency = ${this.baseCurrency})`;
    
    // Section headers
    this.foreignC3Header = `Foreign c3 (Post-1986 Pool, Foreign Source, Functional Currency = ${this.funcCurrency})`;
    this.foreignC3TaxesHeader = `Foreign c3 Taxes (Foreign Source, Currency = ${this.baseCurrency})`;
    this.hdInForeignC3Header = `Hovering Deficits included in Foreign c3 (Post-1986 Pool, Foreign Source, Functional Currency = ${this.funcCurrency})`;
    this.hdInForeignC3TaxesHeader = `Hovering Deficits included in Foreign c3 Taxes (Foreign Source, Currency = ${this.baseCurrency})`;
    this.ptiFCHeader = `PTI (Post - 1986 years, Functional Currency = ${this.funcCurrency})`;
    this.ptiUSDHeader = `PTI USD Basis (Post-1986 years, Currency = ${this.baseCurrency})`;
    this.recapAcctHeader = `Recapture Accounts (Functional Currency = ${this.funcCurrency})`;
    this.qualDefHeader = `Qualified Deficits (Functional Currency = ${this.funcCurrency})`;
    this.otherAttrsHeader = `Other Attributes -- US Source E&P (Post-1986 Pool, Functional Currency = ${this.funcCurrency})`;
    
    this.headerSectionForError = {
      'FOREIGN_C3': this.foreignC3Header,
      'FOREIGN_C3_TAXES': this.foreignC3TaxesHeader,
      'HDEFICIT_FOREIGN_C3': this.hdInForeignC3Header,
      'HDEFICIT_FOREIGN_C3_TAXES': this.hdInForeignC3TaxesHeader,
      'PTI_FC': this.ptiFCHeader,
      'PTI_BC': this.ptiUSDHeader,
      'RECAP_ACCT': this.recapAcctHeader,
      'QUAL_DEF': this.qualDefHeader,
      'OTHERS': this.otherAttrsHeader
    };

    this.labelprospectiveAdjTitle = "A prospective adjustment to GTAPS is an adjustment to your E&P and tax pools that is not reflected in FIR and GHOST Exam scenarios.  Prospective adjustments include adjustments to true up E&P and tax pools due to outsourced E&P studies.  Prospective adjustments should NOT include E&P changes for open QAR years, even if those changes are part of an E&P study.  These changes should be reported in FIR and GHOST Exam scenarios.  GTAPS adjustments for changes that are reported in FIR and GHOST Exam scenarios should not be tagged as prospective adjustments.  For questions, please contact Michael Steward or Michael Roussin.";
  }

  loadGTAPSEditAttrsData() {
    this.showFormLoader = true;
    const params = {
      "ho_cdr_no": this.rowData.GTAPS_CDR_NO,
      "ho_leid": this.rowData.GTAPS_LEID,
      "ho_reporting_period": 1
    };

    this.firEditGtapsAttributesService.getEditAdjustAttrsData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess !== "1") {
          this.gtwSnackBarService.openSnackBar(
            data.errorMessage || 'Error loading data',
            'gtw-snackbar--red'
          );
          this.showFormLoader = false;
        } else {
          this.editAttrsData = data.jsonObject;
          if (this.editAttrsData && this.editAttrsData.length > 0) {
            this.processEditAttrsData();
          } else {
            // No data to process, hide loader
            this.showFormLoader = false;
          }
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        this.gtwSnackBarService.openSnackBar(
          'Error loading data. Please try again.',
          'gtw-snackbar--red'
        );
      }
    );
  }

  processEditAttrsData() {
    const data = this.editAttrsData[0];
    
    // Process tax year begin/end
    if (data.hasOwnProperty("TAX_YEAR_BEGIN_END_JSON")) {
      const taxYrBeginEndJson = JSON.parse(data.TAX_YEAR_BEGIN_END_JSON);
      if (taxYrBeginEndJson.length > 0) {
        if (taxYrBeginEndJson[0].hasOwnProperty("TAX_YEAR_BEGIN")) {
          this.taxYearBegin = taxYrBeginEndJson[0].TAX_YEAR_BEGIN;
        }
        if (taxYrBeginEndJson[0].hasOwnProperty("TAX_YEAR_END")) {
          this.taxYearEnd = taxYrBeginEndJson[0].TAX_YEAR_END;
          this.gtapsBalanceHeader = "Balance per GTAPS " + this.taxYearEnd;
        }
      }
    }

    // Process adjustment types
    if (data.hasOwnProperty("ADJ_TYPE_JSON")) {
      const adjTypeJson = JSON.parse(data.ADJ_TYPE_JSON);
      if (adjTypeJson.length > 0) {
        this.gtapsAdjustmentTypes = adjTypeJson;
        this.adjTypeSL = [];
        this.adjTypeTaxesSL = [];
        
        adjTypeJson.forEach((adjType: any) => {
          if (adjType.CODE_NAME && adjType.CODE_DESC) {
            if (adjType.CODE_NAME === '905' || adjType.CODE_NAME === 'ADJ') {
              this.adjTypeSL.push({'CODE_NAME': adjType.CODE_NAME, 'CODE_DESC': adjType.CODE_DESC});
              this.adjTypeTaxesSL.push({'CODE_NAME': adjType.CODE_NAME, 'CODE_DESC': adjType.CODE_DESC});
            } else if (adjType.CODE_NAME === '90M') {
              this.adjTypeTaxesSL.push({'CODE_NAME': adjType.CODE_NAME, 'CODE_DESC': adjType.CODE_DESC});
            }
          }
        });
      }
    }

    // Process tax year list
    if (data.hasOwnProperty("TAX_YEAR_LIST_JSON")) {
      const taxYearListJson = JSON.parse(data.TAX_YEAR_LIST_JSON);
      if (taxYearListJson.length > 0) {
        this.taxYearSL = [];
        taxYearListJson.forEach((taxYear: any) => {
          if (taxYear.TAX_YEAR) {
            const index = this.taxYearSL.findIndex(taxYearRow => 
              taxYearRow.label === taxYear.TAX_YEAR && taxYearRow.value === taxYear.TAX_YEAR);
            if (index === -1) {
              this.taxYearSL.push({label: taxYear.TAX_YEAR, value: taxYear.TAX_YEAR});
            }
          }
        });
      }
    }

    // Process ghost scenarios
    if (data.hasOwnProperty("GHOST_SCENARIOS_JSON")) {
      const ghostScenariosJson = JSON.parse(data.GHOST_SCENARIOS_JSON);
      if (ghostScenariosJson.length > 0) {
        ghostScenariosJson.forEach((ghostScenarioRow: any) => {
          if (ghostScenarioRow.SCENARIO_DESC && ghostScenarioRow.SCENARIO_DESC.toUpperCase() === 'EXAM') {
            this.examGhostScenario = ghostScenarioRow.GHOST_SCENARIO;
          } else if (ghostScenarioRow.SCENARIO_DESC && ghostScenarioRow.SCENARIO_DESC.toUpperCase() === 'RETURN AS FILED') {
            this.rafGhostScenario = ghostScenarioRow.GHOST_SCENARIO;
          }
        });
      }
    }

    // Process headings
    if (data.hasOwnProperty("HEADING_JSON")) {
      const headingJson = JSON.parse(data.HEADING_JSON);
      if (headingJson.length > 0) {
        this.gtapsHeadings = {};
        headingJson.forEach((headingRow: any) => {
          if (headingRow.hasOwnProperty("BASKET_CODE") && headingRow.hasOwnProperty("BASKET_DESC")) {
            this.gtapsHeadings[headingRow.BASKET_CODE] = headingRow.BASKET_DESC;
          }
        });
      }
    }

    // Process recapture accounts
    if (data.hasOwnProperty("GET_RECAPTURE_ACCT_JSON")) {
      const getRecaptureAcctJson = JSON.parse(data.GET_RECAPTURE_ACCT_JSON);
      if (getRecaptureAcctJson.length > 0 && getRecaptureAcctJson[0].hasOwnProperty("RECAPTURE_ACCT_COUNT")) {
        this.recaptureAccounts = getRecaptureAcctJson[0].RECAPTURE_ACCT_COUNT > 0 ? "Yes" : "No";
      }
    }

    // Process qualified deficits
    if (data.hasOwnProperty("GET_QUAL_DEF_JSON")) {
      const getQualDefJson = JSON.parse(data.GET_QUAL_DEF_JSON);
      if (getQualDefJson.length > 0 && getQualDefJson[0].hasOwnProperty("QUAL_DEF_COUNT")) {
        this.qualifiedDeficits = getQualDefJson[0].QUAL_DEF_COUNT > 0 ? "Yes" : "No";
      }
    }

    // Process PTI basis amount
    if (data.hasOwnProperty("GET_PTI_BASIS_AMT_JSON")) {
      const getPTIBasisAmtJson = JSON.parse(data.GET_PTI_BASIS_AMT_JSON);
      if (getPTIBasisAmtJson.length > 0 && getPTIBasisAmtJson[0].hasOwnProperty("PTI_BASIS_TOTAL_AMT")) {
        this.totalUSDBasisInPti = getPTIBasisAmtJson[0].PTI_BASIS_TOTAL_AMT;
      }
    }

    // Process EP summary info
    if (data.hasOwnProperty("EP_SUMMARY_INFO_JSON")) {
      const epSummaryInfoJson = JSON.parse(data.EP_SUMMARY_INFO_JSON);
      if (epSummaryInfoJson.length > 0) {
        const summaryInfo = epSummaryInfoJson[0];
        if (summaryInfo.hasOwnProperty("POST_1986C3EP_FC")) {
          this.total959c3EPPoolFuncCurr = summaryInfo.POST_1986C3EP_FC;
        }
        if (summaryInfo.hasOwnProperty("POST_1986C3EPUSD")) {
          this.total959c3EPPoolUSD = summaryInfo.POST_1986C3EPUSD;
        }
        if (summaryInfo.hasOwnProperty("POST_1986C3TAXES")) {
          this.totalTaxPool = summaryInfo.POST_1986C3TAXES;
        }
        if (summaryInfo.hasOwnProperty("ETRONPOST_1986C3EP")) {
          this.effectiveRateOn959c3TotalEp = summaryInfo.ETRONPOST_1986C3EP;
        }
        if (summaryInfo.hasOwnProperty("PTIFC")) {
          this.totalPtiFuncCurr = summaryInfo.PTIFC;
        }
        if (summaryInfo.hasOwnProperty("PTIUSD")) {
          this.totalPtiUSD = summaryInfo.PTIUSD;
        }
      }
    }

    // Process amounts data
    if (data.hasOwnProperty("AMOUNTS_JSON")) {
      this.processAmountsData(JSON.parse(data.AMOUNTS_JSON));
    }
    
    // Hide loader after all data processing is complete
    this.showFormLoader = false;
  }

  processAmountsData(amountsJson: any[]) {
    if (amountsJson.length === 0) return;


    // Sort amounts JSON based on SORT_ORDER, FTC_ID Ascending (same as original)
    amountsJson.sort((a, b) => {
      if (a.hasOwnProperty("SORT_ORDER") && b.hasOwnProperty("SORT_ORDER") && a.SORT_ORDER === b.SORT_ORDER) {
        if (a.hasOwnProperty("FTC_ID") && b.hasOwnProperty("FTC_ID")) {
          return a.FTC_ID - b.FTC_ID;
        }
        return 0;
      } else if (a.hasOwnProperty("SORT_ORDER") && b.hasOwnProperty("SORT_ORDER")) {
        return a.SORT_ORDER - b.SORT_ORDER;
      }
      return 0;
    });

    let currentSortOrderTotals: any[] = [];
    let prevSortOrder: any = null;
    let headerKey: string = '';
    let ptiType: string | null = null;
    let epTax: string | null = null;
    let hoveringDeficit: string | null = null;
    let adjCode: string | null = null;
    let adjYr: string | null = null;
    let i = 0;

    // Process each row (following original AngularJS logic exactly)
    amountsJson.forEach((amtRow) => {
      // Check if we need to process the previous group (when sort order changes)
      if (prevSortOrder !== null && amtRow.hasOwnProperty("SORT_ORDER") && prevSortOrder !== amtRow.SORT_ORDER) {
        // Process the previous group
        if (currentSortOrderTotals.length > 0) {
          this.createEditableTotalsForHeader(headerKey, currentSortOrderTotals, adjCode, adjYr);
        }
        currentSortOrderTotals = [];
      }

      // Reset variables for current row (following original logic)
      let basketCode = this.labelTotal;
      let basketDesc = this.labelTotal;
      let amt: number | null = null;
      epTax = null;
      ptiType = null;
      hoveringDeficit = null;
      let editedTotalAmt: number | undefined = undefined;
      adjCode = null;
      adjYr = null;
      
      prevSortOrder = amtRow.SORT_ORDER;

      if (amtRow.hasOwnProperty("SORT_ORDER")) {
        // Handle basket code and description
        if (amtRow.hasOwnProperty("BASKET_CODE") && amtRow.BASKET_CODE && amtRow.BASKET_CODE.trim() !== '' && this.gtapsHeadings && this.gtapsHeadings[amtRow.BASKET_CODE]) {
          basketDesc = this.gtapsHeadings[amtRow.BASKET_CODE];
          basketCode = amtRow.BASKET_CODE;
        }
        if (amtRow.hasOwnProperty("QUAL_DESCRIPTION") && amtRow.QUAL_DESCRIPTION && amtRow.QUAL_DESCRIPTION !== 'NONE' && this.gtapsHeadings && this.gtapsHeadings[amtRow.QUAL_DESCRIPTION]) {
          basketDesc = this.gtapsHeadings[amtRow.QUAL_DESCRIPTION];
          basketCode = amtRow.QUAL_DESCRIPTION;
        }
        
        // Extract other properties
        if (amtRow.hasOwnProperty("EP_TAX")) {
          epTax = amtRow.EP_TAX;
        }
        if (amtRow.hasOwnProperty("TOTAL_AMT")) {
          amt = amtRow.TOTAL_AMT;
        }
        if (amtRow.hasOwnProperty("PTI_TYPE")) {
          ptiType = amtRow.PTI_TYPE;
        }
        if (amtRow.hasOwnProperty("HDEFICIT")) {
          hoveringDeficit = amtRow.HDEFICIT;
        }

        // Determine header key for this row
        headerKey = this.getHeaderKey(ptiType, epTax, hoveringDeficit);
        
        // Set adjustment codes based on PTI_TYPE (following original logic)
        if (ptiType) {
          adjCode = (ptiType === "C1" || ptiType === "C2" || ptiType === "QD" || ptiType === "RR") ? 'ADJ' : null;
          adjYr = (ptiType === "C1") ? 'Post-1986' : null;
        }

        // For Basket Code - "Total" set EDITED_TOTAL_AMT default value as 0
        if (basketCode === this.labelTotal) {
          editedTotalAmt = 0;
        }

        const rowData = {
          "BASKET_CODE": basketCode,
          "BASKET_DESC": basketDesc,
          "EP_TAX": epTax,
          "TOTAL_AMT": amt,
          "EDITED_TOTAL_AMT": editedTotalAmt,
          "NINEOHFOURDCODE": amtRow.NINEOHFOURDCODE,
          "PASSIVECODE": amtRow.PASSIVECODE,
          "SUBFCODE": amtRow.SUBFCODE,
          "FPHCICODE": amtRow.FPHCICODE,
          "QUAL_DESCRIPTION": amtRow.QUAL_DESCRIPTION,
          "PTI_TYPE": ptiType,
          "SOURCE": amtRow.SOURCE,
          "HDEFICIT": hoveringDeficit,
          "EP_TAX_TYPE": epTax === "T" ? "U" : "P", // Fixed: T = Tax = U, E = E&P = P
          "CONVENTION": ptiType === "C3" ? "P" : "L"
        };

        currentSortOrderTotals.push(rowData);
      }
      i++;
    });

    if (currentSortOrderTotals.length > 0 && headerKey) {
      this.createEditableTotalsForHeader(headerKey, currentSortOrderTotals, adjCode, adjYr);
    }

  }

  getHeaderKey(ptiType: string | null, epTax: string | null, hoveringDeficit: string | null): string {
    if (ptiType === 'C3') {
      if (hoveringDeficit && hoveringDeficit === 'Y') {
        return (epTax === 'E') ? "HDEFICIT_FOREIGN_C3" : "HDEFICIT_FOREIGN_C3_TAXES";
      } else {
        return (epTax === 'E') ? "FOREIGN_C3" : "FOREIGN_C3_TAXES";
      }
    } else if (ptiType === 'C2') {
      return "PTI_FC";
    } else if (ptiType === 'C1') {
      return "PTI_BC";
    } else if (ptiType === 'RR') {
      return "RECAP_ACCT";
    } else if (ptiType === 'QD') {
      return "QUAL_DEF";
    } else if (ptiType === 'P') {
      return "OTHERS";
    }
    return '';
  }

  createEditableTotalsForHeader(headerKey: string, currentSortOrderTotals: any[], adjCode: string | null, adjYr: string | null) {
    if (!headerKey || currentSortOrderTotals.length === 0) return;

    this.allTotals[headerKey] = currentSortOrderTotals;
    
    // Create two editable rows and one totals row for each header section (exactly like original)
    const totalsRow = JSON.parse(JSON.stringify(currentSortOrderTotals));
    const editableFirstRow = JSON.parse(JSON.stringify(currentSortOrderTotals));
    const editableSecondRow = JSON.parse(JSON.stringify(currentSortOrderTotals));
    
    // Match original AngularJS structure exactly
    this.editableTotals[headerKey] = {
      0: {"ADJ_TYPE": adjCode, "ADJ_YEAR": adjYr, "PTI_TYPE": null, "OWN_SUB": null, "AMOUNT_DATA": totalsRow},
      1: {"ADJ_TYPE": adjCode, "ADJ_YEAR": adjYr, "PTI_TYPE": null, "OWN_SUB": null, "AMOUNT_DATA": editableFirstRow},
      2: {"ADJ_TYPE": adjCode, "ADJ_YEAR": adjYr, "PTI_TYPE": null, "OWN_SUB": null, "AMOUNT_DATA": editableSecondRow}
    };

    // For Totals row, default EDITED_TOTAL_AMT value to TOTAL_AMT (exactly like original)
    if (this.editableTotals[headerKey][0]) {
      this.editableTotals[headerKey][0].AMOUNT_DATA.forEach((editableAmtRow: any) => {
        editableAmtRow.EDITED_TOTAL_AMT = editableAmtRow.TOTAL_AMT;
      });
    }
    
  }

  amountFieldOnChange(headerKey: string, value: any, rowNo: number, currentBasketDesc: string) {
    // Add this headerKey, rowNo, basketDesc combination to list of dirtied rows if value is not equal to 0
    if (value && value != 0) {
      if (!this.changedHeaderRowBaskets) {
        this.changedHeaderRowBaskets = [];
      }
      const index = this.changedHeaderRowBaskets.findIndex(changedHeaderRow => 
        (changedHeaderRow.HEADER_KEY === headerKey && changedHeaderRow.ROW_NO === rowNo && changedHeaderRow.BASKET === currentBasketDesc));
      // don't push it if it already exists
      if (index === -1) {
        this.changedHeaderRowBaskets.push({"HEADER_KEY": headerKey, "ROW_NO": rowNo, "BASKET": currentBasketDesc});
      }
    } else {
      // Remove this headerKey, rowNo, currentBasketDesc combination from changedHeaderRowBaskets
      if (this.changedHeaderRowBaskets) {
        const index = this.changedHeaderRowBaskets.findIndex(changedHeaderRow => 
          (changedHeaderRow.HEADER_KEY === headerKey && changedHeaderRow.ROW_NO === rowNo && changedHeaderRow.BASKET === currentBasketDesc));
        if (index !== -1) {
          this.changedHeaderRowBaskets.splice(index, 1);
        }
      }
    }

    // Update all Totals related to the passed headerKey section
    if (this.editableTotals[headerKey][rowNo] && this.editableTotals[headerKey][0]) {
      let index = 0;
      const basketColTotal: any = {};
      const rowTotal: any = {};
      let basketDesc: string;
      let allRowTotal = 0;

      Object.keys(this.editableTotals[headerKey]).forEach(key => {
        const headerEditableRow = this.editableTotals[headerKey][key];
        if (index !== 0) {
          headerEditableRow.AMOUNT_DATA.forEach((amtColumn: any) => {
            basketDesc = amtColumn["BASKET_DESC"];
            if (basketDesc !== 'Total' && amtColumn["EDITED_TOTAL_AMT"] !== undefined) {
              if (amtColumn.EDITED_TOTAL_AMT || amtColumn.EDITED_TOTAL_AMT === 0) {
                // For Column total
                if (basketColTotal[basketDesc] === undefined) {
                  basketColTotal[basketDesc] = parseInt(amtColumn.EDITED_TOTAL_AMT);
                } else {
                  basketColTotal[basketDesc] = parseInt(basketColTotal[basketDesc]) + parseInt(amtColumn.EDITED_TOTAL_AMT);
                }
                // For row total
                if (rowTotal[index] === undefined) {
                  rowTotal[index] = parseInt(amtColumn.EDITED_TOTAL_AMT);
                } else {
                  rowTotal[index] = parseInt(rowTotal[index]) + parseInt(amtColumn.EDITED_TOTAL_AMT);
                }
              }
            }
          });
          // Set Row level Total for each Row
          headerEditableRow.AMOUNT_DATA.forEach((amtColumn: any) => {
            basketDesc = amtColumn["BASKET_DESC"];
            if (basketDesc && basketDesc === 'Total' && rowTotal[index] !== undefined) {
              amtColumn.EDITED_TOTAL_AMT = parseInt(rowTotal[index]);
            }
          });
        }
        index++;
      });

      // Set it in the Column level Totals Row (i.e. index 0 of editableRows for current headerKey)
      this.editableTotals[headerKey][0].AMOUNT_DATA.forEach((totalAmtRow: any) => {
        basketDesc = totalAmtRow["BASKET_DESC"];
        if (basketDesc && basketDesc === 'Total') {
          Object.keys(rowTotal).forEach(key => {
            allRowTotal = parseInt(allRowTotal.toString()) + parseInt(rowTotal[key]);
          });
          totalAmtRow.EDITED_TOTAL_AMT = parseInt(totalAmtRow.TOTAL_AMT) + parseInt(allRowTotal.toString());
        } else if (currentBasketDesc === basketDesc && 
                   totalAmtRow["TOTAL_AMT"] !== undefined && 
                   (basketColTotal[basketDesc] || basketColTotal[basketDesc] === 0)) {
          totalAmtRow.EDITED_TOTAL_AMT = parseInt(totalAmtRow.TOTAL_AMT) + parseInt(basketColTotal[basketDesc]);
          if (headerKey === 'HDEFICIT_FOREIGN_C3' && totalAmtRow.EDITED_TOTAL_AMT > 0) {
            this.gtwSnackBarService.openSnackBar(
              this.totalHFC3NegativeError,
              'gtw-snackbar--yellow'
            );
          } else if (headerKey === 'HDEFICIT_FOREIGN_C3_TAXES' && totalAmtRow.EDITED_TOTAL_AMT < 0) {
            this.gtwSnackBarService.openSnackBar(
              this.totalHC3TaxesPositiveError,
              'gtw-snackbar--yellow'
            );
          }
        }
      });
    }
  }

  validateForm(): boolean {
    let errorText = null;
    if (!this.changedHeaderRowBaskets || this.changedHeaderRowBaskets.length === 0) {
      errorText = this.noChangesError;
    } else if (!this.adjDesc || this.adjDesc.trim() === '') {
      errorText = this.adjDescError;
    } else {
      let headerKey: string;
      let rowNo: number;
      for (let i = 0; i < this.changedHeaderRowBaskets.length; i++) {
        headerKey = this.changedHeaderRowBaskets[i].HEADER_KEY;
        rowNo = this.changedHeaderRowBaskets[i].ROW_NO;
        if (this.editableTotals[headerKey][rowNo].ADJ_YEAR !== undefined &&
            (!this.editableTotals[headerKey][rowNo].ADJ_YEAR || this.editableTotals[headerKey][rowNo].ADJ_YEAR === '')) {
          errorText = this.selectTaxYearError + this.headerSectionForError[headerKey] + this.adjustmentError + rowNo;
          break;
        } else if (this.editableTotals[headerKey][rowNo].ADJ_TYPE !== undefined &&
                   (!this.editableTotals[headerKey][rowNo].ADJ_TYPE || this.editableTotals[headerKey][rowNo].ADJ_TYPE === '')) {
          errorText = this.selectAdjTypeError + this.headerSectionForError[headerKey] + this.adjustmentError + rowNo;
          break;
        } else if (headerKey === 'PTI_FC' &&
                   (this.editableTotals[headerKey][rowNo].OWN_SUB !== undefined &&
                   (!this.editableTotals[headerKey][rowNo].OWN_SUB || this.editableTotals[headerKey][rowNo].OWN_SUB === ''))) {
          errorText = this.selectOwnSubError + this.headerSectionForError[headerKey] + this.adjustmentError + rowNo;
          break;
        } else if (headerKey === 'PTI_FC' &&
                   (this.editableTotals[headerKey][rowNo].PTI_TYPE !== undefined &&
                   (!this.editableTotals[headerKey][rowNo].PTI_TYPE || this.editableTotals[headerKey][rowNo].PTI_TYPE === ''))) {
          errorText = this.selectPtiTypeError + this.headerSectionForError[headerKey] + this.adjustmentError + rowNo;
          break;
        }
      }
    }
    if (errorText && errorText.trim() !== '') {
      this.gtwSnackBarService.openSnackBar(
        errorText,
        'gtw-snackbar--red'
      );
      return false;
    }
    return true;
  }

  reset() {
    this.crudLoading = false;
    this.adjDesc = '';
    this.isG2G = 'Y';
    this.changedHeaderRowBaskets = [];

    Object.keys(this.editableTotals).forEach(headerKey => {
      let ptiType: string | null = null;
      if (this.allTotals[headerKey][0] && this.allTotals[headerKey][0].PTI_TYPE) {
        ptiType = this.allTotals[headerKey][0].PTI_TYPE;
      }

      Object.keys(this.editableTotals[headerKey]).forEach(childKey => {
        let adjCode: string | null = null;
        let adjYr: string | null = null;
        if (childKey !== '0' && ptiType) {
          adjCode = (ptiType === "C1" || ptiType === "C2" || ptiType === "QD" || ptiType === "RR") ? 'ADJ' : adjCode;
          adjYr = (ptiType === "C1") ? 'Post-1986' : adjYr;
        }
        this.editableTotals[headerKey][childKey] = {
          "ADJ_TYPE": adjCode,
          "ADJ_YEAR": adjYr,
          "PTI_TYPE": null,
          "OWN_SUB": null,
          "AMOUNT_DATA": JSON.parse(JSON.stringify(this.allTotals[headerKey]))
        };
      });

      // For Totals row, default EDITED_TOTAL_AMT value to TOTAL_AMT
      if (this.editableTotals[headerKey][0]) {
        this.editableTotals[headerKey][0].AMOUNT_DATA.forEach((editableAmtRow: any) => {
          editableAmtRow.EDITED_TOTAL_AMT = editableAmtRow.TOTAL_AMT;
        });
      }
    });
  }

  save() {
    if (this.crudLoading) {
      this.gtwSnackBarService.openSnackBar(
        'Please wait while we save this request',
        'gtw-snackbar--yellow'
      );
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Validate the Form before saving the data
    if (this.validateForm()) {
      // Populate the editAttributesJson records
      const editAttributesJson: any = {};
      
      this.changedHeaderRowBaskets.forEach(changedHeaderRowBasket => {
        const headerKey = changedHeaderRowBasket.HEADER_KEY;
        const rowNo = changedHeaderRowBasket.ROW_NO;
        const basket = changedHeaderRowBasket.BASKET;
        
        if (this.editableTotals[headerKey][rowNo].AMOUNT_DATA) {
          if (!editAttributesJson["EDIT_ROWS"]) {
            editAttributesJson["EDIT_ROWS"] = [];
          }
          
          const amtData = this.editableTotals[headerKey][rowNo].AMOUNT_DATA;
          for (let i = 0; i < amtData.length; i++) {
            if (amtData[i].BASKET_DESC && amtData[i].BASKET_DESC === basket) {
              const editedPtiType = this.editableTotals[headerKey][rowNo].PTI_TYPE;
              
                editAttributesJson["EDIT_ROWS"].push({
                  "HEADER": headerKey,
                  "ADJ_DESC": this.adjDesc,
                  "G2G_YORN": this.isG2G,
                  "ADJ_CODE": this.editableTotals[headerKey][rowNo].ADJ_TYPE,
                  "ADJ_YEAR": this.editableTotals[headerKey][rowNo].ADJ_YEAR ? this.editableTotals[headerKey][rowNo].ADJ_YEAR.toString() : null,
                  "EDITED_PTI_TYPE": editedPtiType,
                  "PTI_TYPE": amtData[i].PTI_TYPE,
                  "OWN_SUB": this.editableTotals[headerKey][rowNo].OWN_SUB,
                  "BASKET_CODE": amtData[i].BASKET_CODE,
                  "BASKET_DESC": this.gtapsHeadings[amtData[i].BASKET_DESC],
                  "EP_TAX": amtData[i].EP_TAX,
                  "AMT": amtData[i].EDITED_TOTAL_AMT,
                  "SOURCE": amtData[i].SOURCE,
                  "HDEFICIT": amtData[i].HDEFICIT,
                  "NINEOHFOURDCODE": amtData[i].NINEOHFOURDCODE,
                  "PASSIVECODE": amtData[i].PASSIVECODE,
                  "SUBFCODE": amtData[i].SUBFCODE,
                  "FPHCICODE": amtData[i].FPHCICODE,
                  "QUAL_DESCRIPTION": amtData[i].QUAL_DESCRIPTION
                });
            }
          }
        }
      });

      const jsonSettings = [{
        "ho_leid": this.hoLeid,
        "ho_cdr_no": this.hoTaxCode,
        "tax_year": this.globalService.tax_year,
        "ghost_scenario": this.examGhostScenario,
        "scenario": this.filterParams.scenario,
        "jcd_key": this.globalService.jcd_key
      }];

      this.firEditGtapsAttributesService.saveGTAPSAttributes(
        this.GENERAL_CONFIG.base_url,
        jsonSettings,
        editAttributesJson,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          
          if (data.callSuccess === "1") {
            // Update allTotals with edited values
            Object.keys(this.editableTotals).forEach(headerKey => {
              if (this.editableTotals[headerKey][0] && this.editableTotals[headerKey][0].AMOUNT_DATA) {
                this.editableTotals[headerKey][0].AMOUNT_DATA.forEach((basketRelatedColumn: any, childKey: number) => {
                  if (basketRelatedColumn.EDITED_TOTAL_AMT !== undefined) {
                    this.allTotals[headerKey][childKey].TOTAL_AMT = basketRelatedColumn.EDITED_TOTAL_AMT;
                  }
                });
              }
            });
            
            this.reset();
            this.gtwSnackBarService.openSnackBar(
              this.saveSuccess,
              'gtw-snackbar--green'
            );
            
            // Emit 'save-success' to refresh grid but keep modal open (matching original behavior)
            this.requestGridUpdate.emit('save-success');
          } else {
            this.gtwSnackBarService.openSnackBar(
              data.errorMessage || 'Save failed',
              'gtw-snackbar--red'
            );
          }
        },
        (error: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackBarService.openSnackBar(
            'Error occurred while saving. Please try again.',
            'gtw-snackbar--red'
          );
        }
      );
    } else {
      this.isSaveClicked = false;
      this.crudLoading = false;
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper method to get object keys for template iteration
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

}